import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card, Button, Table, Modal, Form, Input, Switch,
  Space, Popconfirm, message, Tag, Upload, Alert
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, HolderOutlined, FileWordOutlined, UploadOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getCourse, updateCourse,
  getCourseTopics, createCourseTopic, updateCourseTopic, deleteCourseTopic
} from '../api/courses';
import api from '../api/axios';
import { apiErrorMessage } from '../utils/apiError';

const SortableRow = ({ topic, onEdit, onDelete, courseId, onUpdate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: topic.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#f0f0f0' : 'white',
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td className="p-3 border-b w-10">
        <HolderOutlined
          {...attributes}
          {...listeners}
          style={{ cursor: 'grab', color: '#999', fontSize: 16 }}
        />
      </td>
      <td className="p-3 border-b w-16 text-gray-500">{topic.order_index}</td>
      <td className="p-3 border-b">{topic.name}</td>
      <td className="p-3 border-b">
        <Switch
          checked={topic.is_active}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          style={{ backgroundColor: topic.is_active ? '#52c41a' : '#ff4d4f' }}
          onChange={async (checked) => {
            try {
              await updateCourseTopic(courseId, topic.id, { is_active: checked });
              message.success('Durum güncellendi.');
              onUpdate();
            } catch (error) {
              message.error('Durum güncellenemedi.');
            }
          }}
        />
      </td>
      <td className="p-3 border-b">
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => onEdit(topic)}>
            Düzenle
          </Button>
          <Popconfirm
            title="Projeyi silmek istediğinize emin misiniz?"
            onConfirm={() => onDelete(topic.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button icon={<DeleteOutlined />} danger size="small">Sil</Button>
          </Popconfirm>
        </Space>
      </td>
    </tr>
  );
};

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromBranchId = location.state?.fromBranchId;

  const [course, setCourse] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateUploading, setTemplateUploading] = useState(false);
  const [topicForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCourse();
    fetchTopics();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const res = await getCourse(id);
      setCourse(res.data);
    } catch (error) {
      message.error('Sertifika Programı bilgileri yüklenemedi.');
    }
  };

  const fetchTopics = async () => {
    setLoading(true);
    try {
      const res = await getCourseTopics(id);
      setTopics(res.data);
    } catch (error) {
      message.error('Projeler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = topics.findIndex(t => t.id === active.id);
    const newIndex = topics.findIndex(t => t.id === over.id);
    const newTopics = arrayMove(topics, oldIndex, newIndex);

    const updatedTopics = newTopics.map((topic, index) => ({
      ...topic,
      order_index: index + 1,
    }));
    setTopics(updatedTopics);

    try {
      await Promise.all(
        updatedTopics.map(topic =>
          updateCourseTopic(id, topic.id, { order_index: topic.order_index })
        )
      );
      message.success('Sıralama güncellendi.');
    } catch (error) {
      message.error('Sıralama güncellenemedi.');
      fetchTopics();
    }
  };

  const handleEditCourse = async (values) => {
    try {
      await updateCourse(id, values);
      message.success('Sertifika Programı güncellendi.');
      setEditModalOpen(false);
      fetchCourse();
    } catch (error) {
      message.error('Güncelleme başarısız.');
    }
  };

  const handleTopicSubmit = async (values) => {
    try {
      if (editingTopic) {
        await updateCourseTopic(id, editingTopic.id, values);
        message.success('Proje güncellendi.');
      } else {
        await createCourseTopic(id, values);
        message.success('Proje oluşturuldu.');
      }
      setTopicModalOpen(false);
      topicForm.resetFields();
      setEditingTopic(null);
      fetchTopics();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  const handleTopicEdit = (topic) => {
    setEditingTopic(topic);
    topicForm.setFieldsValue(topic);
    setTopicModalOpen(true);
  };

  const handleTemplateUpload = async ({ file }) => {
    const formData = new FormData();
    formData.append('template', file);
    setTemplateUploading(true);
    try {
      await api.post(`/courses/${id}/certificate-template`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success('Sertifika şablonu yüklendi.');
      setTemplateModalOpen(false);
      fetchCourse();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Şablon yüklenemedi.'));
    } finally {
      setTemplateUploading(false);
    }
    return false;
  };

  const handleTopicDelete = async (topicId) => {
    try {
      await deleteCourseTopic(id, topicId);
      message.success('Proje silindi.');
      fetchTopics();
    } catch (error) {
      message.error('Proje silinemedi.');
    }
  };

  if (!course) return <div className="p-8 text-center">Yükleniyor...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => fromBranchId
              ? navigate(`/branches/${fromBranchId}`, { state: { activeTab: 'courses' } })
              : navigate('/courses')
            }
          >
            Geri
          </Button>
          <h2 className="text-2xl font-bold mb-0">{course.name}</h2>
          <Tag color={course.is_active ? 'green' : 'red'}>
            {course.is_active ? 'Aktif' : 'Pasif'}
          </Tag>
        </div>
        <Space>
          <Button
            icon={<FileWordOutlined />}
            onClick={() => setTemplateModalOpen(true)}
          >
            Sertifika Şablonu
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              editForm.setFieldsValue(course);
              setEditModalOpen(true);
            }}
          >
            Sertifika Programını Düzenle
          </Button>
        </Space>
      </div>

      {course.description && (
        <Card className="mb-4">
          <p className="text-gray-600">{course.description}</p>
        </Card>
      )}

      <Card
        title={`Projeler (${topics.length})`}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            onClick={() => {
              setEditingTopic(null);
              topicForm.resetFields();
              setTopicModalOpen(true);
            }}
          >
            Yeni Proje
          </Button>
        }
      >
        {loading ? (
          <div className="text-center p-4">Yükleniyor...</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={topics.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="p-3 text-left w-10"></th>
                    <th className="p-3 text-left w-16">Sıra</th>
                    <th className="p-3 text-left">Proje Adı</th>
                    <th className="p-3 text-left">Durum</th>
                    <th className="p-3 text-left">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {topics.map((topic) => (
                    <SortableRow
                      key={topic.id}
                      topic={topic}
                      courseId={id}
                      onEdit={handleTopicEdit}
                      onDelete={handleTopicDelete}
                      onUpdate={fetchTopics}
                    />
                  ))}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        )}
        {topics.length === 0 && !loading && (
          <p className="text-gray-400 text-center p-4">Henüz proje eklenmemiş.</p>
        )}
      </Card>

      {/* Sertifika Programı Düzenle Modal */}
      <Modal
        title="Sertifika Programını Düzenle"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        footer={null}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditCourse}>
          <Form.Item name="name" label="Sertifika Programı Adı" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="is_active" label="Durum" valuePropName="checked">
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => setEditModalOpen(false)}>İptal</Button>
              <Button type="primary" htmlType="submit">Güncelle</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Proje Modal */}
      <Modal
        title={editingTopic ? 'Proje Düzenle' : 'Yeni Proje'}
        open={topicModalOpen}
        onCancel={() => {
          setTopicModalOpen(false);
          setEditingTopic(null);
          topicForm.resetFields();
        }}
        footer={null}
      >
        <Form form={topicForm} layout="vertical" onFinish={handleTopicSubmit}>
          <Form.Item name="name" label="Proje Adı" rules={[{ required: true, message: 'Proje adı giriniz.' }]}>
            <Input placeholder="Temel Elektronik" maxLength={100} />
          </Form.Item>
          <Form.Item name="order_index" label="Sıra No" rules={[{ required: true, message: 'Sıra no giriniz.' }]}>
            <Input type="number" min="1" placeholder="1" />
          </Form.Item>
          {editingTopic && (
            <Form.Item name="is_active" label="Durum" valuePropName="checked">
              <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
            </Form.Item>
          )}
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setTopicModalOpen(false); topicForm.resetFields(); }}>İptal</Button>
              <Button type="primary" htmlType="submit">
                {editingTopic ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      {/* Sertifika Şablonu Modal */}
      <Modal
        title="Sertifika Şablonu Yükle"
        open={templateModalOpen}
        onCancel={() => setTemplateModalOpen(false)}
        footer={null}
      >
        {course?.certificate_template && (
          <Alert
            className="mb-4"
            type="success"
            message={
              <div className="flex items-center justify-between">
                <span>
                  Mevcut şablon yüklü
                  <span className="ml-2 text-gray-500 text-xs font-normal">
                    {course.certificate_template.updated_at
                      ? new Date(course.certificate_template.updated_at).toLocaleDateString('tr-TR')
                      : ''}
                  </span>
                </span>
                <Button
                  size="small"
                  icon={<UploadOutlined style={{ transform: 'rotate(180deg)' }} />}
                  onClick={async () => {
                    const res = await api.get(`/courses/${id}/certificate-template/download`, { responseType: 'blob' });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'sertifika-sablonu.docx';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  İndir
                </Button>
              </div>
            }
            description="Yeni dosya yüklerseniz mevcut şablon değiştirilir."
          />
        )}
        <Alert
          className="mb-4"
          type="info"
          message="Kullanılabilir değişkenler"
          description={
            <div className="mt-1 font-mono text-sm space-y-1">
              <div><strong>{'{ad_soyad}'}</strong> — Öğrencinin adı ve soyadı</div>
              <div><strong>{'{sertifika_no}'}</strong> — Sertifika numarası</div>
              <div><strong>{'{tarih}'}</strong> — Sertifika tarihi (GG.AA.YYYY)</div>
              <div><strong>{'{qr}'}</strong> — QR doğrulama kodu görseli (istediğin yere koy)</div>
            </div>
          }
        />
        <Upload
          accept=".docx"
          showUploadList={false}
          customRequest={handleTemplateUpload}
        >
          <Button icon={<UploadOutlined />} loading={templateUploading} type="primary" block>
            .docx Şablonu Seç ve Yükle
          </Button>
        </Upload>
        <p className="text-gray-400 text-xs mt-2 text-center">Maksimum 5 MB · Sadece .docx formatı kabul edilir</p>
      </Modal>
    </div>
  );
};

export default CourseDetail;